import React, { useState } from 'react';
import './interactive.css';

const VIBE_TRACKS = {
  focus: [
    { title: 'Resonance', artist: 'HOME', bpm: 124, genre: 'Chillwave', match: '98%' },
    { title: 'Weightless', artist: 'Marconi Union', bpm: 60, genre: 'Ambient', match: '95%' },
    { title: 'Pacific Coast Highway', artist: 'Kavinsky', bpm: 118, genre: 'Synthwave', match: '92%' }
  ],
  energy: [
    { title: 'Turbo Killer', artist: 'Carpenter Brut', bpm: 140, genre: 'Darksynth', match: '99%' },
    { title: 'Genesis', artist: 'Justice', bpm: 128, genre: 'Electro House', match: '96%' },
    { title: 'Techno Syndrome', artist: 'The Immortals', bpm: 135, genre: 'Electronic', match: '94%' }
  ],
  coding: [
    { title: 'Subwoofer Lullaby', artist: 'C418', bpm: 110, genre: 'Ambient Game', match: '97%' },
    { title: 'Solaris', artist: 'Solar Fields', bpm: 120, genre: 'Space Ambient', match: '94%' },
    { title: 'Age of Wonder', artist: 'Tycho', bpm: 115, genre: 'Downtempo', match: '93%' }
  ]
};

const SpotifyBuilder = () => {
  const [selectedVibe, setSelectedVibe] = useState('focus');
  const [customPrompt, setCustomPrompt] = useState('Late night debugging sessie met rustgevende synths');
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTracks, setActiveTracks] = useState(VIBE_TRACKS.focus);
  const [geminiThought, setGeminiThought] = useState(
    'Gemini AI analyse: Gebruiker zoekt steady ritme voor hoge concentratie. Matching via Spotify Web API: tracks gefilterd op instrumentalness > 0.8 en acousticness < 0.4.'
  );

  const handleGenerate = (vibeKey) => {
    setSelectedVibe(vibeKey);
    setIsGenerating(true);
    setGeminiThought('Gemini AI analyseert prompt & stemming... Querying Spotify Web API endpoint /v1/recommendations...');
    
    setTimeout(() => {
      setActiveTracks(VIBE_TRACKS[vibeKey] || VIBE_TRACKS.focus);
      setIsGenerating(false);
      if (vibeKey === 'energy') {
        setCustomPrompt('Hoge energie synthwave workout sessie');
        setGeminiThought('Gemini AI: Hoge tempo beat vereist. Spotify Web API tempo: 135 BPM, energy: 0.9 geactiveerd.');
      } else if (vibeKey === 'coding') {
        setCustomPrompt('Rustgevende ambient soundscape voor diepe focus');
        setGeminiThought('Gemini AI: Diepe focus sfeer. Spotify Web API acousticness: 0.6, instrumentalness: 0.95 geactiveerd.');
      } else {
        setCustomPrompt('Late night debugging sessie met rustgevende synths');
        setGeminiThought('Gemini AI: Chillwave & Lo-Fi flow. Spotify Web API valence: 0.7, tempo: 120 BPM geactiveerd.');
      }
    }, 600);
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    if (!customPrompt.trim()) return;
    setIsGenerating(true);
    setGeminiThought(`Gemini AI analyseert custom prompt: "${customPrompt}"... Genereren van Spotify parameters...`);
    setTimeout(() => {
      setIsGenerating(false);
      setGeminiThought(`Gemini AI resultaat: vibe gemapped naar Spotify endpoint (energy: 0.75, valence: 0.6, acousticness: 0.4). 3 top matches geretourneerd.`);
    }, 600);
  };

  return (
    <div className="interactive-box">
      <div className="interactive-header">
        <div className="interactive-title">
          <i className="fab fa-spotify" style={{ color: '#1db954' }}></i> Vibe Suggester – Spotify API + Google Gemini AI
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-color)' }}>Docker Backend</div>
      </div>

      <div className="interactive-content">
        <p style={{ fontSize: '0.84rem', color: 'var(--text-color)', margin: '0 0 0.75rem 0' }}>
          Voer een prompt in of kies een preset vibe om de recommendation engine (Spotify Web API + Google Gemini AI) te testen:
        </p>

        <form onSubmit={handleCustomSubmit} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <input 
            type="text" 
            value={customPrompt} 
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Type bijv: Chille beats voor late night coderen..."
            className="spotify-input"
          />
          <button 
            type="submit" 
            className="ansible-btn" 
            style={{ padding: '0.45rem 0.85rem', fontSize: '0.78rem', whiteSpace: 'nowrap' }}
            disabled={isGenerating}
          >
            Analyseer Prompt
          </button>
        </form>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className={`ansible-btn ${selectedVibe === 'focus' ? '' : 'ansible-btn--secondary'}`}
            style={{ padding: '0.4rem 0.85rem', fontSize: '0.78rem' }}
            onClick={() => handleGenerate('focus')}
            disabled={isGenerating}
          >
            🎧 Deep Focus & Lo-Fi
          </button>
          <button
            type="button"
            className={`ansible-btn ${selectedVibe === 'energy' ? '' : 'ansible-btn--secondary'}`}
            style={{ padding: '0.4rem 0.85rem', fontSize: '0.78rem' }}
            onClick={() => handleGenerate('energy')}
            disabled={isGenerating}
          >
            ⚡ High Energy Synth
          </button>
          <button
            type="button"
            className={`ansible-btn ${selectedVibe === 'coding' ? '' : 'ansible-btn--secondary'}`}
            style={{ padding: '0.4rem 0.85rem', fontSize: '0.78rem' }}
            onClick={() => handleGenerate('coding')}
            disabled={isGenerating}
          >
            🌙 Late Night Coding
          </button>
        </div>

        <div className="interactive-detail-card" style={{ marginBottom: '1rem' }}>
          <div style={{ color: '#0284c7', fontWeight: 600, marginBottom: '0.35rem' }}>
            <i className="fas fa-brain"></i> Google Gemini AI Reasoning:
          </div>
          <div style={{ color: isGenerating ? '#f59e0b' : 'var(--text-color)' }}>
            {geminiThought}
          </div>
        </div>

        <div style={{ fontSize: '0.84rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--title-color)' }}>
          <i className="fab fa-spotify" style={{ color: '#1db954', marginRight: '5px' }}></i>
          Spotify Aanbevelingen ({activeTracks.length} tracks):
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {activeTracks.map((track, i) => (
            <div key={i} className="spotify-track-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div 
                  style={{ 
                    width: '32px', 
                    height: '32px', 
                    background: '#1db954', 
                    borderRadius: '6px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#000',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    flexShrink: 0
                  }}
                >
                  <i className="fas fa-play" style={{ fontSize: '0.75rem' }}></i>
                </div>
                <div>
                  <div className="spotify-track-title">{track.title}</div>
                  <div className="spotify-track-artist">{track.artist} • {track.genre}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: '0.75rem' }}>
                <div className="spotify-track-match">{track.match} Match</div>
                <div className="spotify-track-bpm">{track.bpm} BPM</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SpotifyBuilder;
